import { FormControl, Typography, useTheme } from '@mui/material';
import {
  type ExposureOrGroup,
  type ObjectLiteral,
  useUnderwritingQuestions,
  useUpdatePolicyData,
  useUpdateUwQuestions,
} from 'api/submission';
import {
  FOOD_TRUCK_TYPE_FIELD_CODE,
  HAULS_NON_OWNED_GOODS_VEHICLE,
  NUM_VEHICLES_CODE,
  POL_PIP_MI_TOTAL_MEDICAL_EXPENSES_DEFAULT,
  STATES,
  UW_VEHICLE_GROUP_CODE,
  VEHICLE_TAB_PROGRESSION_BLOCK_CONDITIONS_BY_UW_QUESTION_ANSWER,
} from 'common/constants';
import { displayBackendErrorMessage } from 'components/DisplayBackEndErrorMessage';
import { ExposuresList, ExposuresListItem, ExposuresWrapper } from 'components/ExposuresWrapper';
import { displayToastMessage } from 'components/ToastMessage';
import { FormCheckbox } from 'components/lib/FormCheckbox';
import {
  QuestionEngine,
  type QuestionEngineProps,
  useQuestionEngine,
} from 'components/lib/QuestionEngine';
import { useFormik } from 'formik';
import { useExposuresOrGroupFields } from 'hooks/useExposuresOrGroupFields';
import { useScrollToFirstErroredInput } from 'hooks/useScrollToFirstErroredInput';
import { isEmpty, isEqual, pick } from 'lodash-es';
import { type FC, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useFullscreenLoader } from 'store/features/UI';
import * as yup from 'yup';
import type { IVehicle } from './Vehicles.types';
import { VehicleModal } from './components/Modal';
import type { VehicleModalRef } from './components/Modal/VehicleModal.types';

const category = 'vehicle';

const title = (data: IVehicle) => `${data.veh_year} ${data.veh_make} ${data.veh_model}`;

const Vehicles: FC = () => {
  const { id } = useParams() as { id: string };
  const { setFullscreenLoading } = useFullscreenLoader();
  const themeHook = useTheme();

  const { data: questions } = useUnderwritingQuestions(id, true);
  const { mutateAsync: saveUnderwritingQuestions } = useUpdateUwQuestions(id);

  const vehicleQuestionFields = useMemo(
    () =>
      questions?.flattenQuestions.find((q) => q.code === UW_VEHICLE_GROUP_CODE)?.nested_fields ||
      [],
    [questions?.flattenQuestions],
  );

  const {
    validateFormik: validateVehicleUWQuestionFormik,
    updateState,
    ...questionEngineProps
  } = useQuestionEngine<ObjectLiteral>({ fields: vehicleQuestionFields });

  const {
    formik: vehicleUWFormik,
    state: vehicleUWState,
    ref: questionEngineRef,
  } = questionEngineProps;

  const {
    relatedResource: vehicleExposures,
    performAction,
    submission,
    submissionId,
  } = useExposuresOrGroupFields<IVehicle>({
    type: 'exposure',
    name: category,
  });

  const isHaulsNonOwnedGoodsVehicle = useMemo(
    () =>
      vehicleExposures.some(
        (exposure) =>
          exposure?.data?.veh_type === 'TTT' && exposure?.data?.veh_subtype !== 'Light Truck',
      ),
    [vehicleExposures],
  );

  const { mutateAsync: updatePolicyData } = useUpdatePolicyData(submissionId);

  const vehicleDrawerRef = useRef<VehicleModalRef>(undefined);

  const handleAdd = async (data: IVehicle) =>
    performAction({
      action: 'add',
      data,
      loadingText: `Adding ${title(data)}`,
    });

  const handleUpdate = async (data: IVehicle, id: string) =>
    performAction({
      action: 'update',
      data,
      resourceId: id,
      loadingText: `Updating the ${title(data)}`,
    });

  const handleDelete = async (vehicle: ExposureOrGroup<IVehicle>) =>
    performAction({
      action: 'delete',
      resourceId: vehicle.id,
      loadingText: `Deleting the ${title(vehicle.data)}`,
    });

  const formik = useFormik({
    initialValues: {
      noVehiclesConfirmed: (submission?.data?.policy_data?.noVehiclesConfirmed as boolean) || false,
      hasVehicles: false,
    },
    validationSchema: yup.object({
      noVehiclesConfirmed: yup.boolean().when('hasVehicles', {
        is: false,
        then: (schema) =>
          schema.oneOf([true], 'Please confirm that no insured vehicles or add a vehicle.'),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
    enableReinitialize: true,
  });

  const renderQuestionCodes = () =>
    questionEngineRef?.current?.renderedQuestions()?.map((question) => question.code) || [];

  const areStatesEqual = () => {
    return isEqual(
      pick(vehicleUWState, renderQuestionCodes()),
      pick(submission?.data?.uw_questions, renderQuestionCodes()),
    );
  };

  useScrollToFirstErroredInput({ formik });

  useEffect(() => {
    const hasVehicles = !isEmpty(vehicleExposures);

    formik.setFieldValue('hasVehicles', hasVehicles, true);
  }, [vehicleExposures]);

  useEffect(() => {
    if (formik.values.hasVehicles) {
      formik.setFieldValue('noVehiclesConfirmed', false, true);
      formik.setTouched({ noVehiclesConfirmed: false });
    }
  }, [formik.values.hasVehicles]);

  useEffect(() => {
    if (!isEmpty(submission?.data)) {
      updateState(submission?.data?.uw_questions || {});
    }
  }, [submission?.data]);

  // eslint-disable-next-line complexity
  const handleOnNext = async () => {
    const isUWQuestionsValid = await validateVehicleUWQuestionFormik();

    if (!isUWQuestionsValid) {
      return false;
    }

    if (!isEmpty(submission?.data?.uw_questions[FOOD_TRUCK_TYPE_FIELD_CODE])) {
      const uwQuestionAnswer = submission?.data?.uw_questions[FOOD_TRUCK_TYPE_FIELD_CODE] as string;

      const blockConditionEntity =
        VEHICLE_TAB_PROGRESSION_BLOCK_CONDITIONS_BY_UW_QUESTION_ANSWER[
          FOOD_TRUCK_TYPE_FIELD_CODE
        ]?.[uwQuestionAnswer];

      if (blockConditionEntity) {
        const result = blockConditionEntity.condition(submission);

        if (result) {
          displayToastMessage('ERROR', blockConditionEntity.validationErrorMessage);
          return false;
        }
      }
    }

    await formik.validateForm();
    formik.setTouched({ noVehiclesConfirmed: true });

    if (!formik.isValid) {
      return false;
    }

    if (
      isEmpty(renderQuestionCodes()) &&
      vehicleUWState?.[HAULS_NON_OWNED_GOODS_VEHICLE] === 'Yes'
    ) {
      try {
        setFullscreenLoading(true, 'Saving data...');

        await saveUnderwritingQuestions({
          id,
          body: {
            ...vehicleUWState,
            [HAULS_NON_OWNED_GOODS_VEHICLE]: 'No',
          },
        });
      } catch (err) {
        displayBackendErrorMessage(err);
        return false;
      } finally {
        setFullscreenLoading(false);
      }
    }

    if (
      formik.values.noVehiclesConfirmed === submission?.data?.policy_data?.noVehiclesConfirmed &&
      !formik.values.hasVehicles
    ) {
      return true;
    }

    try {
      setFullscreenLoading(true, 'Saving data...');

      await updatePolicyData({
        id: submissionId,
        body: {
          ...submission?.data?.policy_data,
          noVehiclesConfirmed: formik.values.noVehiclesConfirmed,
          ...(submission?.us_state === STATES.MI && {
            pol_pip_mi_excess_attendant_care: '',
            pol_pip_mi_total_medical_expenses: formik.values.noVehiclesConfirmed
              ? ''
              : POL_PIP_MI_TOTAL_MEDICAL_EXPENSES_DEFAULT,
          }),
        },
      });

      if (!areStatesEqual()) {
        await saveUnderwritingQuestions({
          id,
          body: pick(vehicleUWState, renderQuestionCodes()),
        });
      }
    } catch (err) {
      displayBackendErrorMessage(err);
      return false;
    } finally {
      setFullscreenLoading(false);
    }

    return true;
  };

  const handleOnPrevious = async () => {
    if (!areStatesEqual()) {
      updateState(submission?.data?.uw_questions || {});
    }

    return true;
  };

  return (
    <>
      <VehicleModal ref={vehicleDrawerRef} handleAdd={handleAdd} handleUpdate={handleUpdate} />
      <ExposuresWrapper
        title="Add your vehicles"
        description="If your business has any vehicles or trailers, please provide some information for each."
        addButtonText={`Add ${category}`}
        onAddBtnClick={() => vehicleDrawerRef?.current?.openModal()}
        onPrevious={handleOnPrevious}
        extraContentAfterAddButton={
          <QuestionEngine
            {...questionEngineProps}
            formik={vehicleUWFormik as QuestionEngineProps['formik']}
            state={{
              isHaulsNonOwnedGoodsVehicle,
              // `numVehicles` gates the pre-bind auto loss-run agreement but never reaches policy_data
              // here, and a missing reference hides the question outright (ENG-12240).
              [NUM_VEHICLES_CODE]: vehicleExposures.length,
              ...vehicleUWState,
              ...submission?.data?.policy_data,
            }}
          />
        }
        onNext={handleOnNext}
      >
        {!formik.values.hasVehicles && (
          <FormControl>
            <FormCheckbox
              id="noVehiclesConfirmed"
              name="noVehiclesConfirmed"
              data-test="noVehiclesConfirmed"
              labelText="I confirm that the business does not intend to insure any vehicles with THREE."
              checked={formik.values.noVehiclesConfirmed}
              onChange={formik.handleChange}
            />
            {formik.touched.noVehiclesConfirmed && formik.errors.noVehiclesConfirmed && (
              <Typography color="error">{formik.errors.noVehiclesConfirmed}</Typography>
            )}
          </FormControl>
        )}

        <ExposuresList>
          {vehicleExposures.map((vehicle) => (
            <ExposuresListItem
              key={vehicle.id}
              category={category}
              title={`${vehicle.data.veh_year} ${vehicle.data.veh_make} ${vehicle.data.veh_model}`}
              onEdit={() => vehicleDrawerRef?.current?.openModal(vehicle)}
              onDelete={() => {
                handleDelete(vehicle);
              }}
            >
              <Typography sx={themeHook.mixins.textOverflowStyle()}>
                {vehicle.data.veh_vin}
              </Typography>
            </ExposuresListItem>
          ))}
        </ExposuresList>
      </ExposuresWrapper>
    </>
  );
};

export default Vehicles;
